import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useDecks } from '../../hooks/useDecks';
import { useMatches } from '../../hooks/useMatches';
import { useFormats } from '../../hooks/useFormats';
import { useStatistics } from '../../hooks/useStatistics';
import { DateRange } from '../../types';
import DateRangeSelector from './DateRangeSelector';
import StatisticsCard from './StatisticsCard';

const StatisticsDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });
  const [selectedDeck, setSelectedDeck] = useState<string>('all');
  const [selectedFormat, setSelectedFormat] = useState<string>('all');

  const { decks } = useDecks();
  const { formats } = useFormats();
  const { matches } = useMatches();
  const { getDeckStatistics, getAllDeckStatistics, getOverallStatistics } = useStatistics(matches, decks);

  // フォーマットに対応するデッキのみをフィルタリング
  const filteredDecks = selectedFormat === 'all' 
    ? decks 
    : decks.filter(deck => deck.formatId === selectedFormat);

  // フォーマット変更時にデッキ選択をリセット
  const handleFormatChange = (formatId: string) => {
    setSelectedFormat(formatId);
    if (formatId !== 'all' && selectedDeck !== 'all') {
      const deckExists = decks.some(deck => deck.id === selectedDeck && deck.formatId === formatId);
      if (!deckExists) {
        setSelectedDeck('all');
      }
    }
  };

  // フォーマットでフィルタリングされたマッチ
  const filteredMatches = selectedFormat === 'all' 
    ? matches 
    : matches.filter(match => match.formatId === selectedFormat);

  const overallStats = getOverallStatistics(dateRange, filteredMatches);
  const allDeckStats = getAllDeckStatistics(dateRange, filteredMatches);
  const selectedDeckStats = selectedDeck !== 'all' ? getDeckStatistics(selectedDeck, dateRange, filteredMatches) : null;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        統計
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          フィルター
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangeSelector 
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>フォーマット</InputLabel>
            <Select
              value={selectedFormat}
              label="フォーマット"
              onChange={(e) => handleFormatChange(e.target.value)}
            >
              <MenuItem value="all">全フォーマット</MenuItem>
              {formats.map((format) => (
                <MenuItem key={format.id} value={format.id}>
                  {format.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }} disabled={selectedFormat !== 'all' && filteredDecks.length === 0}>
            <InputLabel>デッキ</InputLabel>
            <Select
              value={selectedDeck}
              label="デッキ"
              onChange={(e) => setSelectedDeck(e.target.value)}
            >
              <MenuItem value="all">全デッキ</MenuItem>
              {filteredDecks.map((deck) => (
                <MenuItem key={deck.id} value={deck.id}>
                  {deck.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <StatisticsCard
            title="全体統計"
            statistics={overallStats}
            color="primary"
          />
        </Box>

        {selectedDeckStats && (
          <Box sx={{ flex: 1 }}>
            <StatisticsCard
              title={`${selectedDeckStats.deckName}の統計`}
              statistics={selectedDeckStats}
              color="secondary"
            />
          </Box>
        )}
      </Box>

      {selectedDeck === 'all' && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            デッキ別統計
          </Typography>
          
          {allDeckStats.length === 0 ? (
            <Typography variant="body1" color="text.secondary">
              統計データがありません
            </Typography>
          ) : (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 2 
            }}>
              {allDeckStats.map((deckStats) => (
                <StatisticsCard
                  key={deckStats.deckId}
                  title={deckStats.deckName}
                  statistics={deckStats}
                />
              ))}
            </Box>
          )}
        </Paper>
      )}

      {selectedDeckStats && Object.keys(selectedDeckStats.opponentStats).length > 0 && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            対戦相手別統計
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2 
          }}>
            {Object.entries(selectedDeckStats.opponentStats).map(([opponent, stats]) => (
              <StatisticsCard
                key={opponent}
                title={`vs ${opponent}`}
                statistics={stats}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default StatisticsDashboard;